/**
 * ActivityFeedWidget — moje ostatnie akcje z dziennika tenanta (audit_log, wyłącznie wpisy
 * bieżącego usera; bez uprawnień — każdy widzi własne akcje). Wzór Figmy „Ostatnia aktywność":
 * oś z ikoną wg rodzaju zdarzenia (komentarz, akceptacja, plik, ostrzeżenie, zaproszenie),
 * opis zdarzenia i czas względny („2 godz. temu").
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  type LucideIcon,
  MessageSquare,
  Paperclip,
  UserPlus,
} from 'lucide-react';

import { cn } from '@/lib/utils';

import { type ActivityRow, classifyDataError, fetchActivity } from '../dashboard-api';
import type { WidgetRenderProps } from '../types';
import { relativeTime, type Tone } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

interface ActivityKind {
  icon: LucideIcon;
  tone: Tone;
}

/** Rodzaj ikony wg nazwy akcji w dzienniku (`<encja>.<zdarzenie>`). */
export function activityKind(action: string): ActivityKind {
  const a = action.toLowerCase();
  if (/(warning|quota|failed|rejected|error|overdue)/.test(a)) {
    return { icon: AlertTriangle, tone: 'warning' };
  }
  if (a.includes('comment')) return { icon: MessageSquare, tone: 'info' };
  if (/(approved|completed|accepted|posted|paid)/.test(a)) {
    return { icon: CheckCircle2, tone: 'success' };
  }
  if (/(attachment|upload|file|exported)/.test(a)) return { icon: Paperclip, tone: 'info' };
  if (/(invited|user\.)/.test(a)) return { icon: UserPlus, tone: 'purple' };
  return { icon: Activity, tone: 'neutral' };
}

const TONE_CLASS: Record<Tone, string> = {
  accent: 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300',
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

export function ActivityFeedWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const limit = widget.type === 'activity_feed' ? (widget.config.limit ?? 10) : 10;

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'activity', limit],
    queryFn: () => fetchActivity(limit),
    enabled: widget.type === 'activity_feed',
    staleTime: 30_000,
    retry: false,
  });

  if (widget.type !== 'activity_feed') return null;

  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const rows: ActivityRow[] = query.data ?? [];
  if (rows.length === 0)
    return <WidgetEmpty message={t('tenant.dashboard.widgets.activity_feed.empty')} />;

  return (
    <ul className="flex flex-col gap-3">
      {rows.map((r) => {
        const kind = activityKind(r.action);
        const Icon = kind.icon;
        // Znane akcje mają polskie, bezosobowe opisy; nieznane pokazujemy jako kod (czytelny fallback).
        const verb = t(`tenant.dashboard.activity.verbs.${r.action}`, { defaultValue: r.action });
        return (
          <li key={r.id} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={cn(
                'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                TONE_CLASS[kind.tone],
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] leading-snug text-text-primary">
                {verb}
                {r.label && <span className="font-medium"> {r.label}</span>}
              </p>
              <p className="text-[11px] text-text-muted">{relativeTime(r.createdAt)}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
